import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ExternalLink, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore, useArticlesStore, useLikedArticlesStore } from '../store/useStore';
import { social as socialApi, articles as articlesApi } from '../lib/api';
import { renderMentionsAsLinks } from '../utils/renderMentions';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';
import { SharedArticleSkeleton } from './Skeleton';
import toast from 'react-hot-toast';

interface SharedArticle {
  id: string;
  note?: string;
  isPublic: boolean;
  createdAt: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
  };
  article: {
    id: string;
    title: string;
    excerpt?: string;
    url: string;
    imageUrl?: string;
    publishedAt?: string;
    isLiked: boolean;
    feed: {
      title: string;
    };
    _count: {
      comments: number;
      shares: number;
      likes: number;
    };
  };
}

export default function SharedArticles() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user)!;
  const updateArticle = useArticlesStore((state) => state.updateArticle);
  const sharedArticles = useArticlesStore((state) => state.sharedArticles);
  const setSharedArticles = useArticlesStore((state) => state.setSharedArticles);
  const setCurrentContext = useArticlesStore((state) => state.setCurrentContext);
  const { addLikedArticle, removeLikedArticle, isArticleLiked } = useLikedArticlesStore();
  const [sharedArticleShares, setSharedArticleShares] = useState<SharedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [users, setUsers] = useState<any[]>([]);
  const articleRefs = useRef<(HTMLElement | null)[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;

  useEffect(() => {
    async function loadSharedArticles() {
      setLoading(true);
      try {
        const shares = await socialApi.getShares(token, {
          limit: PAGE_SIZE,
          offset: currentOffset
        });
        setSharedArticleShares(shares);
        setHasMore(shares.length === PAGE_SIZE);

        const articles = shares.map(share => share.article);
        setSharedArticles(articles);
        setCurrentContext('shared');

        shares.forEach(share => {
          if (share.article.isLiked) {
            addLikedArticle(share.article.id);
          }
        });

        if (currentOffset > 0) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch {
        toast.error('Failed to load shared articles');
      } finally {
        setLoading(false);
      }
    }

    loadSharedArticles();
  }, [token, currentOffset, addLikedArticle, setSharedArticles, setCurrentContext]);

  useEffect(() => {
    async function loadUsers() {
      try {
        const allUsers = await socialApi.getAllUsers(token);
        setUsers(allUsers);
      } catch (error) {
        console.error('Failed to load users for mentions:', error);
      }
    }

    loadUsers();
  }, [token]);

  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'j':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, sharedArticleShares.length - 1));
          break;
        case 'k':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < sharedArticleShares.length) {
            navigate(`/article/${sharedArticleShares[selectedIndex].article.id}`);
          }
          break;
        case 'l':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < sharedArticleShares.length) {
            const articleId = sharedArticleShares[selectedIndex].article.id;
            handleToggleLike(articleId, isArticleLiked(articleId));
          }
          break;
        case 'escape':
          e.preventDefault();
          setSelectedIndex(-1);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [sharedArticleShares, selectedIndex, navigate, isArticleLiked]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [sharedArticleShares]);

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < articleRefs.current.length) {
      articleRefs.current[selectedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (articleRefs.current.length !== sharedArticleShares.length) {
      articleRefs.current.length = sharedArticleShares.length;
    }
  }, [sharedArticleShares]);

  async function handleToggleLike(articleId: string, isLiked: boolean) {
    try {
      if (isLiked) {
        await articlesApi.unlike(token, articleId);
        removeLikedArticle(articleId);
      } else {
        await articlesApi.like(token, articleId);
        addLikedArticle(articleId);
      }

      setSharedArticleShares(prev =>
        prev.map(share =>
          share.article.id === articleId
            ? { ...share, article: { ...share.article, isLiked: !isLiked } }
            : share
        )
      );

      setSharedArticles(sharedArticles.map(article =>
        article.id === articleId ? { ...article, isLiked: !isLiked } : article
      ));

      updateArticle(articleId, { isLiked: !isLiked });
    } catch {
      toast.error('Failed to update article');
    }
  }

  async function handleUnshare(shareId: string) {
    try {
      await socialApi.deleteShare(token, shareId);

      const shareToRemove = sharedArticleShares.find(share => share.id === shareId);
      setSharedArticleShares(prev => prev.filter(share => share.id !== shareId));

      if (shareToRemove) {
        setSharedArticles(sharedArticles.filter(article => article.id !== shareToRemove.article.id));
      }

      setShowUnshareConfirm(null);
      toast.success('Share removed');
    } catch {
      toast.error('Failed to unshare article');
    }
  }

  function handleMentionClick(username: string) {
    const target = users.find(u => u.username === username);
    if (target) {
      navigate(`/user/${target.id}`);
    }
  }

  function handlePreviousPage() {
    if (currentOffset > 0) {
      setCurrentOffset(prev => Math.max(0, prev - PAGE_SIZE));
      setSelectedIndex(-1);
    }
  }

  function handleNextPage() {
    if (hasMore) {
      setCurrentOffset(prev => prev + PAGE_SIZE);
      setSelectedIndex(-1);
    }
  }

  if (loading) {
    return (
      <div className="bg-canvas">
        <div className="rail"><span className="rail-tab is-active">Friends</span></div>
        <SharedArticleSkeleton count={5} />
      </div>
    );
  }

  return (
    <div className="bg-canvas page-transition min-h-full">
      <div className="rail sticky top-0 z-10 bg-canvas">
        <span className="rail-tab is-active">Friends</span>
        <span className="rail-tab is-quiet border-r-0">Shared with you, newest first</span>
      </div>

      {sharedArticleShares.length === 0 ? (
        <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
          <div className="kicker mb-1">Empty</div>
          <p className="text-ink-secondary">
            Nothing shared yet. When a friend shares an article, it lands here and in your next digest.
          </p>
        </div>
      ) : (
        <div>
          {sharedArticleShares.map((share, index) => {
            const selected = index === selectedIndex;
            const liked = isArticleLiked(share.article.id);
            const mine = share.user.id === user.id;
            return (
              <article
                key={share.id}
                ref={(el) => { articleRefs.current[index] = el; }}
                className={`grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle border-l-2 ${
                  selected ? 'bg-surface-sunken border-l-accent' : 'border-l-transparent hover:bg-surface-sunken'
                }`}
              >
                <div className="pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle flex items-start gap-2">
                  {share.user.avatar ? (
                    <img
                      src={share.user.avatar}
                      alt={share.user.username}
                      className="w-6 h-6 rounded-full object-cover avatar-duotone flex-shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-ink text-canvas flex items-center justify-center font-mono text-[10px] flex-shrink-0">
                      {share.user.username[0].toUpperCase()}
                    </div>
                  )}
                  <span className="stamp hidden lg:inline">{stamp(index + currentOffset)}</span>
                </div>

                <div className="min-w-0 pl-3 lg:pl-4 pr-4 py-2.5 max-w-list">
                  <div className="kicker">
                    <button
                      onClick={() => navigate(`/user/${share.user.id}`)}
                      className="text-ink hover:text-accent-600"
                    >
                      {share.user.displayName || share.user.username}
                    </button>
                    {' · '}{formatAge(share.createdAt)}
                    {' · '}{share.article.feed.title}
                    {share.isPublic && <> · public</>}
                  </div>

                  <h2
                    onClick={() => navigate(`/article/${share.article.id}`)}
                    className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-ink cursor-pointer hover:text-accent-600"
                  >
                    {decodeHtmlEntities(share.article.title)}
                  </h2>

                  {share.note && (
                    <blockquote className="mt-2 pl-3 border-l-2 border-ink text-[15px] text-ink-secondary leading-relaxed">
                      {renderMentionsAsLinks(share.note, handleMentionClick)}
                    </blockquote>
                  )}

                  <div className="flex items-center gap-4 mt-2 kicker">
                    <a
                      href={share.article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 hover:text-ink"
                    >
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                    <button
                      onClick={() => handleToggleLike(share.article.id, liked)}
                      className={`inline-flex items-center gap-1 hover:text-ink ${liked ? 'text-accent-600' : ''}`}
                      title={liked ? 'Unlike' : 'Like'}
                    >
                      <Heart className="w-3 h-3" fill={liked ? 'currentColor' : 'none'} />
                      {liked ? 'Liked' : 'Like'}
                    </button>
                    {share.article._count.comments > 0 && (
                      <span>{share.article._count.comments} {share.article._count.comments === 1 ? 'comment' : 'comments'}</span>
                    )}
                    {share.article._count.likes > 0 && (
                      <span>{share.article._count.likes} {share.article._count.likes === 1 ? 'like' : 'likes'}</span>
                    )}
                    {mine && (
                      <button
                        onClick={() => setShowUnshareConfirm(share.id)}
                        className="inline-flex items-center gap-1 hover:text-signal ml-auto"
                        title="Remove this share"
                      >
                        <Trash2 className="w-3 h-3" /> Unshare
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(currentOffset > 0 || hasMore) && sharedArticleShares.length > 0 && (
        <div className="flex items-stretch border-t border-edge">
          <button
            onClick={handlePreviousPage}
            disabled={currentOffset === 0 || loading}
            className="tool h-12 pl-4 lg:pl-6"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Previous {PAGE_SIZE}
          </button>
          <span className="tool h-12 text-ink-muted">
            {currentOffset + 1} – {currentOffset + sharedArticleShares.length}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!hasMore || loading}
            className="tool h-12"
          >
            Next {PAGE_SIZE} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {showUnshareConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-canvas border border-edge w-full max-w-md mx-4">
            <div className="px-5 h-12 flex items-center border-b border-edge">
              <span className="kicker text-ink">Remove share</span>
            </div>
            <p className="px-5 py-4 text-ink-secondary">
              This takes the article out of your friends' feeds. It can't be undone.
            </p>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setShowUnshareConfirm(null)} className="btn btn-quiet">
                Cancel
              </button>
              <button onClick={() => handleUnshare(showUnshareConfirm)} className="btn btn-danger">
                Unshare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
